// This module manages a singleton WebSocket connection for live tracking, sos triggers.
// only one connection is open at a time and allows multiple components to subscribe to vehicle tracking and sos triggers.

let socket = null;
let intentionalClose = false;
let reconnectTimer = null;
let reconnectAttempts = 0;
let lastRole = null;
let lastConnectionId = null;
const MAX_RECONNECT_ATTEMPTS = 50;
const subscribers = new Set();

const wssUrls = {
  CORPORATEADMIN: process.env.REACT_APP_CORP_WEBSOCKET_URL,
  SUPERADMIN: process.env.REACT_APP_CORP_WEBSOCKET_URL,
  TENANTADMIN: process.env.REACT_APP_CORP_WEBSOCKET_URL,
  VENDOR: process.env.REACT_APP_VENDOR_WEBSOCKET_URL,
  PUBLIC: process.env.REACT_APP_PUBLIC_WEBSOCKET_URL,
};

const wssKeys = {
  VENDOR: 'vendorId',
  CORPORATEADMIN: 'corporateId',
  SUPERADMIN: 'corporateId',
  TENANTADMIN: 'corporateId',
  PUBLIC: 'tripId'
}

function getReconnectDelay() {
  // Exponential backoff: 1s, 2s, 4s, 8s, capped at 30s
  return Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
}

function scheduleReconnect() {
  if (intentionalClose || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
  const delay = getReconnectDelay();
  reconnectTimer = setTimeout(() => {
    reconnectAttempts++;
    connect(lastRole, lastConnectionId);
  }, delay);
}

// establishes a WebSocket connection
function connect(role, connectionId) {

  if (socket && socket.readyState === WebSocket.OPEN) {
    return;
  }

  // Clean up any existing broken socket
  if (socket) {
    try { socket.close(); } catch (_) {}
    socket = null;
  }

  const wssUrl = wssUrls[role];
  const key = wssKeys[role];

  if (!wssUrl) {
    console.error('WebSocket URL not found for role:', role);
    return;
  }

  // Store for reconnection
  lastRole = role;
  lastConnectionId = connectionId;
  intentionalClose = false;

  socket = new WebSocket(wssUrl);

  socket.onopen = () => {
    console.log('WebSocket connection established: ', wssUrl);
    reconnectAttempts = 0; // Reset on successful connection

    const message = {
      type: 'subscribe',
      [key]: connectionId
    };

    socket.send(JSON.stringify(message));
  };

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);

      if (process.env.REACT_APP_MODE === 'staging'){
        console.log("WebSocket message received: ", data);
      }

      // Notify all subscribers of the new data.
      subscribers.forEach((callback) => callback(data));
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  socket.onerror = (error) => {
    console.error('WebSocket error:', error);
  };

  socket.onclose = () => {
    console.log('WebSocket connection closed: ', wssUrl);
    socket = null;
    // Auto-reconnect unless disconnect() was called intentionally
    if (!intentionalClose) {
      scheduleReconnect();
    }
  };
}

// disconnects and cleans up the WebSocket connection
function disconnect() {
  intentionalClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempts = 0;
  if (socket) {
    console.log('Disconnecting WebSocket connection...');
    socket.close();
    socket = null;
  }

  // Clear all subscribers
  subscribers.clear();
  console.log('All WebSocket subscribers cleared');
}

// gets the current connection status
function getConnectionStatus() {
  if (!socket) {
    return { connected: false, readyState: null, subscribers: subscribers.size };
  }

  return {
    connected: socket.readyState === WebSocket.OPEN,
    readyState: socket.readyState,
    subscribers: subscribers.size
  };
}

// subscribes a component to WebSocket data updates.
function subscribe(callback) {
  subscribers.add(callback);
}

// unsubscribes a component from WebSocket data updates.
function unsubscribe(callback) {
  subscribers.delete(callback);
}

export {connect, disconnect, getConnectionStatus, subscribe, unsubscribe};
